import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Inbox, Bell, AlertCircle, ShieldAlert, Search, CheckCircle, Send, Plus, X,
  Calendar, Clock, MapPin, Users, FileText, Award, Stethoscope, Heart, Sparkles,
  Filter, ChevronRight, Check, Mail, Archive, MessageSquare, Eye, RefreshCw, ChevronDown, Shield,
  BarChart3, Lock, Unlock, PenLine, MoonStar, ArchiveRestore
} from 'lucide-react';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../../components/ui/jewel-modal';
import { Badge } from '../../components/ui/badge';
import { NoticeThread } from '../../components/hr/NoticeThread';
import { HRAnalytics } from '../../components/hr/HRAnalytics';
import { StaffHRProfile } from '../../components/hr/StaffHRProfile';
import { TemplateLibrary } from '../../components/hr/TemplateLibrary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon + accent per type
const TYPE_META = {
  sickness:              { icon: Stethoscope, accent: 'amber',   hex: '#F59E0B', bar: 'from-amber-400 to-orange-500' },
  absence:               { icon: Clock,       accent: 'orange',  hex: '#F97316', bar: 'from-orange-400 to-rose-500' },
  warning_verbal:        { icon: AlertCircle, accent: 'amber',   hex: '#F59E0B', bar: 'from-amber-400 to-orange-500' },
  warning_written:       { icon: ShieldAlert, accent: 'rose',    hex: '#F43F5E', bar: 'from-rose-400 to-red-500' },
  warning_final:         { icon: ShieldAlert, accent: 'rose',    hex: '#F43F5E', bar: 'from-rose-500 to-red-600' },
  disciplinary_invite:   { icon: FileText,    accent: 'rose',    hex: '#F43F5E', bar: 'from-rose-400 to-pink-500' },
  investigation_invite:  { icon: FileText,    accent: 'violet',  hex: '#8A2BE2', bar: 'from-violet-400 to-purple-500' },
  absence_review_invite: { icon: Calendar,    accent: 'cyan',    hex: '#00E5FF', bar: 'from-cyan-400 to-sky-500' },
  outcome_dismissal:     { icon: X,           accent: 'rose',    hex: '#F43F5E', bar: 'from-rose-500 to-red-600' },
  outcome_no_action:     { icon: CheckCircle, accent: 'emerald', hex: '#10B981', bar: 'from-emerald-400 to-teal-500' },
  outcome_final_warning: { icon: ShieldAlert, accent: 'rose',    hex: '#F43F5E', bar: 'from-rose-500 to-red-600' },
  outcome_action_plan:   { icon: Sparkles,    accent: 'cyan',    hex: '#00E5FF', bar: 'from-cyan-400 to-violet-500' },
  commendation:          { icon: Award,       accent: 'emerald', hex: '#10B981', bar: 'from-emerald-400 to-cyan-500' },
  note:                  { icon: Mail,        accent: 'slate',   hex: '#94A3B8', bar: 'from-slate-400 to-slate-500' },
};

const getAuthHeaders = () => {
  // Login stores as `staff_token`; keep `token` as a legacy fallback.
  const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

const HRInboxPage = ({ currentUser }) => {
  const [notices, setNotices] = useState([]);
  const [tab, setTab] = useState('inbox'); // inbox | meetings | warnings | outcomes | archived | sent | dashboard | analytics
  const [dashboard, setDashboard] = useState(null);
  const [attachFile, setAttachFile] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [managerNotices, setManagerNotices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [profileUsername, setProfileUsername] = useState(null); // Staff HR profile drawer

  const isManager = ['manager', 'admin', 'executive', 'superadmin'].includes(currentUser?.role);

  const fetchInbox = async () => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hr-notices/inbox`, { headers: h });
      setNotices(r.data.notices || []);
    } catch (e) {}
    setLoading(false);
  };

  const fetchTemplates = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hr-notices/templates`);
      setTemplates(r.data.templates || []);
    } catch {}
  };

  const fetchManagerData = async () => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      // Fetch staff-list unconditionally — backend enforces manager+ access.
      // Overview + dashboard are manager-only so we swallow their 403s.
      const [ov, sd, dash] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/hr-notices/manager/overview`, { headers: h }).catch(() => ({ data: { notices: [] } })),
        // Primary: pulls directly from admin_users (works for every real account).
        // Fallback: the staff_directory collection, only useful if it's been seeded.
        axios.get(`${BACKEND_URL}/api/shifts/staff-list`, { headers: h })
          .catch(() => axios.get(`${BACKEND_URL}/api/staff-directory/`, { headers: h }))
          .catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/hr-notices/manager/dashboard`, { headers: h }).catch(() => ({ data: null })),
      ]);
      setManagerNotices(ov.data?.notices || []);
      const raw = Array.isArray(sd.data)
        ? sd.data
        : (sd.data?.entries || sd.data?.staff || sd.data?.members || []);
      const seen = new Set();
      const list = raw
        .filter(s => s && s.username && s.username !== currentUser?.username)
        .filter(s => (seen.has(s.username) ? false : seen.add(s.username)))
        .sort((a, b) => (a.display_name || a.username).localeCompare(b.display_name || b.username));
      setStaffList(list);
      setDashboard(dash.data || null);
    } catch {}
  };

  useEffect(() => {
    fetchInbox();
    fetchTemplates();
    fetchManagerData();
    // Auto-refresh the inbox + sent view every 30s so replies land in real time.
    const poll = setInterval(() => {
      fetchInbox();
      fetchManagerData();
    }, 30000);
    return () => clearInterval(poll);
  }, [currentUser?.username, currentUser?.role]);

  const markRead = async (id) => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      await axios.post(`${BACKEND_URL}/api/hr-notices/inbox/${id}/read`, {}, { headers: h });
      setNotices(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
  };

  const respond = async (id, action, text = '', proposed_date = null, signature = null) => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      await axios.post(`${BACKEND_URL}/api/hr-notices/inbox/${id}/respond`, { action, response_text: text, proposed_date, signature }, { headers: h });
      toast.success(action === 'acknowledge' ? 'Acknowledged' : action === 'accept_meeting' ? 'Meeting accepted' : action === 'request_reschedule' ? 'Reschedule requested' : 'Response sent');
      fetchInbox();
      setSelected(null);
    } catch {
      toast.error('Failed to send response');
    }
  };

  const openNotice = (n) => {
    setSelected(n);
    if (!n.is_read) markRead(n.id);
  };

  // Tabs source & filter
  const filtered = useMemo(() => {
    // Hide archived items from every list except the Archived tab.
    let items = tab === 'archived' ? notices.filter(n => n.archived) : notices.filter(n => !n.archived);
    if (tab === 'meetings') items = items.filter(n => ['disciplinary_invite', 'investigation_invite', 'absence_review_invite'].includes(n.type));
    else if (tab === 'warnings') items = items.filter(n => n.type.startsWith('warning_'));
    else if (tab === 'outcomes') items = items.filter(n => n.type.startsWith('outcome_') || n.type === 'commendation');
    if (query) {
      const q = query.toLowerCase();
      items = items.filter(n => (n.title || '').toLowerCase().includes(q) || (n.description || '').toLowerCase().includes(q) || (n.type_label || '').toLowerCase().includes(q));
    }
    return items;
  }, [notices, tab, query]);

  const unread = notices.filter(n => !n.is_read && !n.archived).length;
  const pendingAck = notices.filter(n => n.requires_ack && !n.acknowledged && !n.archived).length;
  const archivedCount = notices.filter(n => n.archived).length;

  // Snooze / archive / close actions
  const snoozeNotice = async (id, until) => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      await axios.post(`${BACKEND_URL}/api/hr-notices/inbox/${id}/snooze`, { until }, { headers: h });
      toast.success('Snoozed'); fetchInbox(); setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to snooze'); }
  };
  const archiveNotice = async (id) => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      await axios.post(`${BACKEND_URL}/api/hr-notices/inbox/${id}/archive`, {}, { headers: h });
      toast.success('Archived'); fetchInbox(); setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to archive'); }
  };
  const unarchiveNotice = async (id) => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      await axios.post(`${BACKEND_URL}/api/hr-notices/inbox/${id}/unarchive`, {}, { headers: h });
      toast.success('Restored'); fetchInbox();
    } catch { toast.error('Failed to restore'); }
  };
  const closeCase = async (id) => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      await axios.post(`${BACKEND_URL}/api/hr-notices/notice/${id}/close`, {}, { headers: h });
      toast.success('Case closed'); fetchManagerData(); setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to close'); }
  };
  const reopenCase = async (id) => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      await axios.post(`${BACKEND_URL}/api/hr-notices/notice/${id}/reopen`, {}, { headers: h });
      toast.success('Case reopened'); fetchManagerData(); setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reopen'); }
  };

  return (
    <div className="space-y-6 relative">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute -top-32 left-1/4 w-[500px] h-[300px] bg-cyan-500/[0.08] rounded-full blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[280px] bg-violet-500/[0.06] rounded-full blur-[120px]" />
      </div>

      {/* Jewel Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0a0a14] via-[#0d0b1f] to-[#0a0a14] p-6 md:p-7">
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] bg-cyan-500/[0.10] rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-violet-500/[0.08] rounded-full blur-[100px]" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="relative w-14 h-14 flex-shrink-0">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/30" />
              <div className="absolute inset-0 rounded-2xl flex items-center justify-center">
                <Inbox className="w-7 h-7 text-white" strokeWidth={1.5} />
              </div>
              {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 border-2 border-[#0a0a14] flex items-center justify-center text-white text-[10px] font-bold shadow-[0_0_10px_rgba(244,63,94,0.6)] animate-pulse">{unread}</span>}
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-1">People · HR</p>
              <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight">HR <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">Inbox</span></h1>
              <p className="text-white/50 text-sm mt-1">Sickness · absences · warnings · meeting invites · outcomes</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {pendingAck > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/[0.10] border border-amber-500/30 text-amber-300 text-[11px]">
                <AlertCircle className="w-3.5 h-3.5" /> {pendingAck} awaiting acknowledgement
              </span>
            )}
            {isManager && (
              <button onClick={() => setShowCompose(true)} data-testid="hr-compose-btn"
                className="group relative inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 text-white text-xs font-semibold shadow-[0_10px_30px_-10px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all overflow-hidden">
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Plus className="w-3.5 h-3.5" /> New notice
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
          {[
            ...(isManager ? [{ id: 'dashboard', label: 'Dashboard', icon: FileText, count: dashboard?.totals?.total_cases || 0, hex: '#00E5FF' }] : []),
            { id: 'inbox', label: 'All', icon: Inbox, count: notices.filter(n => !n.archived).length, hex: '#00E5FF' },
            { id: 'meetings', label: 'Meetings', icon: Calendar, count: notices.filter(n => !n.archived && ['disciplinary_invite', 'investigation_invite', 'absence_review_invite'].includes(n.type)).length, hex: '#8A2BE2' },
            { id: 'warnings', label: 'Warnings', icon: ShieldAlert, count: notices.filter(n => !n.archived && n.type.startsWith('warning_')).length, hex: '#F43F5E' },
            { id: 'outcomes', label: 'Outcomes', icon: Award, count: notices.filter(n => !n.archived && (n.type.startsWith('outcome_') || n.type === 'commendation')).length, hex: '#10B981' },
            ...(archivedCount > 0 ? [{ id: 'archived', label: 'Archived', icon: Archive, count: archivedCount, hex: '#94A3B8' }] : []),
            ...(isManager ? [{ id: 'sent', label: 'Sent', icon: Send, count: managerNotices.length, hex: '#F59E0B' }] : []),
            ...(isManager ? [{ id: 'analytics', label: 'Analytics', icon: BarChart3, count: null, hex: '#EC4899' }] : []),
          ].map(t => {
            const a = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`hr-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all ${
                  a ? 'text-white border border-white/20' : 'text-white/50 hover:text-white border border-transparent'
                }`}
                style={a ? { background: `linear-gradient(135deg, ${t.hex}22, ${t.hex}0a)`, boxShadow: `0 0 14px -4px ${t.hex}` } : {}}>
                <t.icon className="w-3 h-3" style={a ? { color: t.hex } : {}} />
                <span>{t.label}</span>
                {t.count !== null && <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold ${a ? 'bg-white/20 text-white' : 'bg-white/[0.04] text-white/50'}`}>{t.count}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-w-[200px] max-w-md relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search notices, titles, types…"
            data-testid="hr-inbox-search"
            className="w-full pl-11 pr-4 py-2.5 bg-white/[0.02] border border-white/[0.06] rounded-full text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-400/40" />
        </div>
      </div>

      {/* Notice list */}
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md p-4">
        {loading ? (
          <div className="text-center py-12 text-white/40 text-sm">Loading inbox…</div>
        ) : tab === 'dashboard' && isManager ? (
          <ManagerDashboard data={dashboard} onOpen={openNotice} onOpenStaff={setProfileUsername} />
        ) : tab === 'analytics' && isManager ? (
          <HRAnalytics onOpenStaff={setProfileUsername} />
        ) : tab === 'sent' && isManager ? (
          <ManagerSentList notices={managerNotices} onRefresh={fetchManagerData} onOpenStaff={setProfileUsername} onOpenNotice={openNotice} />
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/[0.08] border border-emerald-500/20 mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-300" strokeWidth={1.5} />
            </div>
            <p className="text-white/70 tracking-wide">Inbox zero</p>
            <p className="text-white/35 text-xs mt-1">Nothing to review right now.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(n => {
              const meta = TYPE_META[n.type] || TYPE_META.note;
              const Icon = meta.icon;
              return (
                <button key={n.id} onClick={() => openNotice(n)}
                  data-testid={`hr-notice-${n.id}`}
                  className={`w-full text-left relative overflow-hidden rounded-2xl border transition-all p-4 flex items-start gap-3 hover:border-white/[0.15] ${
                    n.is_read ? 'border-white/[0.06] bg-white/[0.02]' : 'border-cyan-400/25 bg-gradient-to-br from-cyan-500/[0.05] via-white/[0.02] to-transparent shadow-[0_0_22px_-10px_rgba(0,229,255,0.5)]'
                  }`}>
                  <span className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${meta.bar}`} />
                  <div className="w-10 h-10 rounded-xl border border-white/[0.08] flex items-center justify-center flex-shrink-0"
                    style={{ background: `${meta.hex}20`, boxShadow: `inset 0 1px 0 rgba(255,255,255,0.05), 0 0 14px -6px ${meta.hex}` }}>
                    <Icon className="w-4 h-4" style={{ color: meta.hex }} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.1em] uppercase border" style={{ color: meta.hex, background: `${meta.hex}12`, borderColor: `${meta.hex}30` }}>
                        {n.type_label}
                      </span>
                      {!n.is_read && <span className="inline-flex items-center gap-1 text-cyan-300 text-[9px] tracking-[0.15em] uppercase"><span className="w-1 h-1 rounded-full bg-cyan-400 animate-pulse" /> New</span>}
                      {n.requires_ack && !n.acknowledged && <span className="inline-flex items-center gap-1 text-amber-300 text-[9px] tracking-[0.15em] uppercase"><AlertCircle className="w-2.5 h-2.5" /> Ack required</span>}
                      {n.acknowledged && <span className="inline-flex items-center gap-1 text-emerald-300 text-[9px] tracking-[0.15em] uppercase"><Check className="w-2.5 h-2.5" /> Acknowledged</span>}
                    </div>
                    <p className={`text-sm font-medium truncate ${n.is_read ? 'text-white/85' : 'text-white'}`}>{n.title}</p>
                    <p className="text-white/45 text-[11px] mt-0.5 line-clamp-2">{n.description}</p>
                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/35">
                      <span>From {n.created_by_name}</span>
                      <span>·</span>
                      <span>{new Date(n.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      {n.meeting_date && <><span>·</span><span className="text-cyan-300"><Calendar className="w-2.5 h-2.5 inline mr-0.5" />{new Date(n.meeting_date).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span></>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30 mt-1 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Notice detail modal */}
      {selected && (
        <NoticeDetailModal
          notice={selected}
          currentUser={currentUser}
          isManager={isManager}
          onClose={() => setSelected(null)}
          onRespond={respond}
          onSnooze={snoozeNotice}
          onArchive={archiveNotice}
          onUnarchive={unarchiveNotice}
          onClose_={closeCase}
          onReopen={reopenCase}
          onOpenStaff={setProfileUsername}
          onRefresh={() => { fetchInbox(); fetchManagerData(); }}
        />
      )}

      {/* Staff HR profile drawer */}
      {profileUsername && (
        <StaffHRProfile
          username={profileUsername}
          onClose={() => setProfileUsername(null)}
          onOpenNotice={(n) => { setProfileUsername(null); setTimeout(() => openNotice(n), 100); }}
        />
      )}

      {/* Compose modal (manager only) */}
      {isManager && (
        <HRComposeModal open={showCompose} onOpenChange={setShowCompose} templates={templates} staffList={staffList} currentUser={currentUser} onSent={() => { fetchManagerData(); setShowCompose(false); }} />
      )}
    </div>
  );
};

/* ─────────────────────────────  DETAIL MODAL  ───────────────────────────── */
const NoticeDetailModal = ({ notice, currentUser, isManager, onClose, onRespond, onSnooze, onArchive, onUnarchive, onClose_, onReopen, onOpenStaff, onRefresh }) => {
  const meta = TYPE_META[notice.type] || TYPE_META.note;
  const [response, setResponse] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [signature, setSignature] = useState('');
  const [showSnooze, setShowSnooze] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState('');
  const isMeeting = ['disciplinary_invite', 'investigation_invite', 'absence_review_invite'].includes(notice.type);
  const isWarning = notice.type?.startsWith('warning_') || notice.type === 'outcome_final_warning';
  const canAck = notice.requires_ack && !notice.acknowledged;
  const iAmRecipient = notice.staff_username === currentUser?.username;
  const iAmAuthor = notice.created_by === currentUser?.username;
  const canCloseCase = isManager && notice.status !== 'closed';
  const canReopen = isManager && notice.status === 'closed';

  const handleAck = () => {
    // For warnings, require typed signature for audit trail
    if (isWarning && !signature.trim()) {
      toast.error('Please type your full name to sign the acknowledgement');
      return;
    }
    onRespond(notice.id, 'acknowledge', response, null, signature.trim() || null);
  };

  return (
    <JewelModal open={true} onOpenChange={(v) => !v && onClose()}
      icon={meta.icon} title={notice.title} subtitle={notice.type_label} accent={meta.accent} size="2xl" testid="hr-notice-detail"
      footer={<>
        <JewelButton variant="ghost" onClick={onClose}>Close</JewelButton>
        {/* Staff-side actions */}
        {iAmRecipient && !notice.archived && notice.acknowledged && !notice.requires_ack === false && (
          <JewelButton variant="ghost" onClick={() => onArchive(notice.id)} icon={Archive} data-testid="hr-archive-btn">Archive</JewelButton>
        )}
        {iAmRecipient && !notice.archived && !canAck && (
          <JewelButton variant="ghost" onClick={() => setShowSnooze(s => !s)} icon={MoonStar} data-testid="hr-snooze-btn">Snooze</JewelButton>
        )}
        {iAmRecipient && notice.archived && (
          <JewelButton variant="ghost" onClick={() => onUnarchive(notice.id)} icon={ArchiveRestore} data-testid="hr-unarchive-btn">Restore</JewelButton>
        )}
        {/* Manager case controls */}
        {canCloseCase && iAmAuthor && (
          <JewelButton variant="ghost" onClick={() => onClose_(notice.id)} icon={Lock} data-testid="hr-close-case-btn">Close case</JewelButton>
        )}
        {canReopen && iAmAuthor && (
          <JewelButton variant="ghost" onClick={() => onReopen(notice.id)} icon={Unlock} data-testid="hr-reopen-case-btn">Reopen</JewelButton>
        )}
        {/* Meeting actions */}
        {isMeeting && iAmRecipient && !notice.acknowledged && (
          <>
            <JewelButton variant="ghost" onClick={() => onRespond(notice.id, 'request_reschedule', response, proposedDate)}>Request reschedule</JewelButton>
            <JewelButton accent="emerald" onClick={() => onRespond(notice.id, 'accept_meeting', response)} icon={Check}>Accept meeting</JewelButton>
          </>
        )}
        {/* Acknowledge action */}
        {!isMeeting && canAck && iAmRecipient && (
          <JewelButton accent={meta.accent} onClick={handleAck} icon={isWarning ? PenLine : Check} data-testid="hr-acknowledge-btn">
            {isWarning ? 'Sign & acknowledge' : 'Acknowledge'}
          </JewelButton>
        )}
      </>}
    >
      <div className="space-y-4">
        {/* Snooze picker */}
        {showSnooze && iAmRecipient && (
          <div className="p-3 rounded-2xl bg-violet-500/[0.05] border border-violet-500/25 flex items-center gap-2 flex-wrap" data-testid="hr-snooze-picker">
            <MoonStar className="w-3.5 h-3.5 text-violet-300" />
            <p className="text-violet-200 text-xs">Snooze until:</p>
            <input type="date" value={snoozeDate} onChange={(e) => setSnoozeDate(e.target.value)} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
              data-testid="hr-snooze-date"
              className="px-2 py-1 bg-black/40 border border-white/[0.08] rounded-lg text-white text-xs focus:outline-none focus:border-violet-400/50" />
            <JewelButton size="sm" accent="violet" onClick={() => onSnooze(notice.id, snoozeDate)} disabled={!snoozeDate} data-testid="hr-snooze-confirm">Snooze</JewelButton>
            <JewelButton size="sm" variant="ghost" onClick={() => setShowSnooze(false)}>Cancel</JewelButton>
          </div>
        )}

        {/* Case status ribbon */}
        {(notice.status === 'closed' || notice.archived || notice.snoozed_until) && (
          <div className="flex items-center gap-2 flex-wrap">
            {notice.status === 'closed' && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-500/[0.10] border border-slate-500/25 text-slate-300 text-[10px] font-semibold">
                <Lock className="w-3 h-3" /> Case closed {notice.closed_at ? `· ${new Date(notice.closed_at).toLocaleDateString('en-GB')}` : ''}
              </span>
            )}
            {notice.archived && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/60 text-[10px] font-semibold">
                <Archive className="w-3 h-3" /> Archived
              </span>
            )}
            {notice.snoozed_until && new Date(notice.snoozed_until) > new Date() && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-500/[0.10] border border-violet-500/25 text-violet-300 text-[10px] font-semibold">
                <MoonStar className="w-3 h-3" /> Snoozed until {new Date(notice.snoozed_until).toLocaleDateString('en-GB')}
              </span>
            )}
          </div>
        )}

        {/* Meta row */}
        <div className="flex items-center gap-3 flex-wrap p-3 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
              <span className="text-white text-xs font-semibold">{(notice.created_by_name || '?').charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <p className="text-white/85 text-sm">{notice.created_by_name}</p>
              <p className="text-white/40 text-[10px]">Sent {new Date(notice.created_at).toLocaleString('en-GB')}</p>
            </div>
          </div>
          {isManager && notice.staff_username && !iAmRecipient && (
            <button onClick={() => onOpenStaff && onOpenStaff(notice.staff_username)}
              data-testid="hr-detail-view-profile"
              className="ml-1 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-cyan-500/[0.08] border border-cyan-500/25 text-cyan-300 hover:bg-cyan-500/[0.15] text-[10px] font-medium transition-all">
              <Eye className="w-3 h-3" /> View {notice.staff_display_name || notice.staff_username}'s HR profile
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {notice.acknowledged && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/[0.10] border border-emerald-500/25 text-emerald-300 text-[10px]"><Check className="w-3 h-3" /> Acknowledged</span>}
            {notice.response && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/[0.10] border border-cyan-500/25 text-cyan-300 text-[10px]"><CheckCircle className="w-3 h-3" /> Responded</span>}
          </div>
        </div>

        {notice.description && (
          <div className="rounded-2xl bg-white/[0.02] border border-white/[0.05] p-4">
            <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-2">Details</p>
            <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{notice.description}</p>
          </div>
        )}

        {isMeeting && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {notice.meeting_date && (
              <div className="p-3 rounded-xl bg-cyan-500/[0.05] border border-cyan-500/20">
                <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> Meeting date</p>
                <p className="text-white text-sm font-medium">{new Date(notice.meeting_date).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            )}
            {notice.meeting_location && (
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> Location</p>
                <p className="text-white/85 text-sm">{notice.meeting_location}</p>
              </div>
            )}
            {notice.meeting_attendees?.length > 0 && (
              <div className="md:col-span-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1.5 flex items-center gap-1"><Users className="w-2.5 h-2.5" /> Attendees</p>
                <div className="flex flex-wrap gap-1.5">
                  {notice.meeting_attendees.map((a, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-cyan-500/[0.08] border border-cyan-500/20 text-cyan-300 text-[11px]">{a}</span>)}
                </div>
              </div>
            )}
            {notice.right_to_be_accompanied && (
              <div className="md:col-span-2 p-3 rounded-xl bg-violet-500/[0.06] border border-violet-500/20">
                <p className="text-violet-200 text-xs"><Shield className="w-3 h-3 inline mr-1" />You have the right to be accompanied by a colleague or trade union representative.</p>
              </div>
            )}
          </div>
        )}

        {notice.allegations && (
          <div className="p-4 rounded-2xl bg-rose-500/[0.05] border border-rose-500/20">
            <p className="text-[9px] tracking-[0.2em] uppercase text-rose-300 mb-2">Allegations</p>
            <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{notice.allegations}</p>
          </div>
        )}
        {notice.evidence && (
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
            <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-2">Evidence & context</p>
            <p className="text-white/85 text-sm whitespace-pre-wrap">{notice.evidence}</p>
          </div>
        )}

        {(notice.absence_start || notice.absence_end) && (
          <div className="p-3 rounded-2xl bg-amber-500/[0.05] border border-amber-500/20">
            <p className="text-[9px] tracking-[0.2em] uppercase text-amber-300 mb-1">Absence period</p>
            <p className="text-white/85 text-sm">{notice.absence_start} → {notice.absence_end}</p>
            {notice.absence_reason && <p className="text-white/60 text-xs mt-1">Reason: {notice.absence_reason}</p>}
            <div className="flex gap-3 mt-2 text-[10px] text-white/50">
              {notice.self_certified && <span className="inline-flex items-center gap-1"><Check className="w-2.5 h-2.5" /> Self-certified</span>}
              {notice.fit_note && <span className="inline-flex items-center gap-1"><FileText className="w-2.5 h-2.5" /> Fit note provided</span>}
            </div>
          </div>
        )}

        {notice.action_plan && (
          <div className="p-4 rounded-2xl bg-emerald-500/[0.05] border border-emerald-500/20">
            <p className="text-[9px] tracking-[0.2em] uppercase text-emerald-300 mb-2">Action plan</p>
            <p className="text-white/85 text-sm whitespace-pre-wrap">{notice.action_plan}</p>
          </div>
        )}

        {notice.attachments?.length > 0 && (
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> Attachments</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {notice.attachments.map((a, i) => (
                <a key={i} href={`${BACKEND_URL}${a.url}`} target="_blank" rel="noopener noreferrer" data-testid={`hr-attachment-${i}`}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-cyan-400/25 transition-all group">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/[0.10] border border-cyan-500/25 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-3.5 h-3.5 text-cyan-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/85 text-xs truncate group-hover:text-white">{a.name}</p>
                    <p className="text-white/40 text-[10px] font-mono">{(a.size / 1024).toFixed(0)} KB · {a.type?.toUpperCase()}</p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-cyan-300" />
                </a>
              ))}
            </div>
          </div>
        )}

        {(isMeeting || canAck) && (
          <JewelField label="Your response (optional)">
            <JewelTextarea rows={3} value={response} onChange={(e) => setResponse(e.target.value)} placeholder="Add any notes, questions, or availability preferences…" data-testid="hr-response-text" />
          </JewelField>
        )}
        {isMeeting && (
          <JewelField label="Propose an alternative date (only if requesting reschedule)">
            <JewelInput type="datetime-local" value={proposedDate} onChange={(e) => setProposedDate(e.target.value)} />
          </JewelField>
        )}
        {isWarning && canAck && (
          <div className="p-4 rounded-2xl bg-rose-500/[0.05] border border-rose-500/25 space-y-2">
            <p className="text-[9px] tracking-[0.2em] uppercase text-rose-300 font-semibold flex items-center gap-1.5">
              <PenLine className="w-3 h-3" /> Digital signature required
            </p>
            <p className="text-white/60 text-[11px]">By typing your full name below and signing, you formally acknowledge you have read and understood this warning. Your signature is timestamped and recorded in the audit log.</p>
            <JewelInput value={signature} onChange={(e) => setSignature(e.target.value)}
              placeholder="Type your full legal name to sign"
              data-testid="hr-signature-input"
              className="font-serif italic text-base tracking-wide" />
            {signature.trim() && (
              <p className="text-emerald-300 text-[10px] font-mono">✓ Signing as "{signature.trim()}" at {new Date().toLocaleString('en-GB')}</p>
            )}
          </div>
        )}
        {notice.signature && notice.acknowledged && (
          <div className="p-3 rounded-2xl bg-emerald-500/[0.05] border border-emerald-500/25">
            <p className="text-[9px] tracking-[0.2em] uppercase text-emerald-300 mb-1 flex items-center gap-1"><PenLine className="w-2.5 h-2.5" /> Signed acknowledgement</p>
            <p className="text-white font-serif italic text-base tracking-wide">{notice.signature}</p>
            <p className="text-white/45 text-[10px] font-mono mt-1">Signed {new Date(notice.signed_at || notice.acknowledged_at).toLocaleString('en-GB')}</p>
          </div>
        )}

        {/* Threaded conversation - available on all notices */}
        <NoticeThread notice={notice} currentUser={currentUser} onUpdate={onRefresh} />
      </div>
    </JewelModal>
  );
};

/* ─────────────────────────────  MANAGER SENT LIST  ───────────────────────────── */
const ManagerSentList = ({ notices, onRefresh, onOpenStaff, onOpenNotice }) => {
  const [filter, setFilter] = useState('all'); // all | awaiting | responded | acknowledged
  const [expanded, setExpanded] = useState(new Set());

  const toggle = (id) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const stats = {
    total: notices.length,
    awaiting: notices.filter(n => !n.response && !n.acknowledged).length,
    responded: notices.filter(n => n.response && !n.acknowledged).length,
    acknowledged: notices.filter(n => n.acknowledged).length,
    read: notices.filter(n => n.is_read).length,
  };

  const filtered = notices.filter(n => {
    if (filter === 'awaiting')     return !n.response && !n.acknowledged;
    if (filter === 'responded')    return n.response && !n.acknowledged;
    if (filter === 'acknowledged') return n.acknowledged;
    return true;
  });

  if (!notices.length) {
    return (
      <div className="text-center py-14">
        <div className="w-14 h-14 rounded-2xl bg-white/[0.03] ring-1 ring-white/[0.06] flex items-center justify-center mx-auto mb-3">
          <Send className="w-5 h-5 text-white/30" />
        </div>
        <p className="text-white/50 text-sm font-semibold mb-1">No notices issued yet</p>
        <p className="text-white/30 text-xs">Tap New Notice above to send your first.</p>
      </div>
    );
  }

  const filterChips = [
    { id: 'all',          label: 'All',          count: stats.total,        tone: 'text-white/70 ring-white/15' },
    { id: 'awaiting',     label: 'Awaiting',     count: stats.awaiting,     tone: 'text-amber-300 ring-amber-500/30' },
    { id: 'responded',    label: 'Responded',    count: stats.responded,    tone: 'text-cyan-300 ring-cyan-500/30' },
    { id: 'acknowledged', label: 'Acknowledged', count: stats.acknowledged, tone: 'text-emerald-300 ring-emerald-500/30' },
  ];

  return (
    <div className="space-y-3">
      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Sent',         value: stats.total,        hex: '#94A3B8' },
          { label: 'Read',         value: stats.read,         hex: '#00E5FF' },
          { label: 'Responded',    value: stats.responded + stats.acknowledged, hex: '#10B981' },
          { label: 'Awaiting',     value: stats.awaiting,     hex: '#F59E0B' },
        ].map(s => (
          <div key={s.label} className="rounded-xl bg-white/[0.02] ring-1 ring-white/[0.06] p-2.5">
            <p className="text-[9px] uppercase tracking-[0.14em] font-bold" style={{ color: `${s.hex}CC` }}>{s.label}</p>
            <p className="text-white text-lg font-bold tabular-nums leading-none mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter chip row + refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        {filterChips.map(c => (
          <button
            key={c.id}
            onClick={() => setFilter(c.id)}
            data-testid={`sent-filter-${c.id}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ring-1 transition-all ${
              filter === c.id ? `${c.tone} bg-white/[0.06]` : 'text-white/40 ring-white/10 hover:text-white/70 hover:ring-white/20'
            }`}
          >
            {c.label}
            <span className="tabular-nums opacity-70">{c.count}</span>
          </button>
        ))}
        {onRefresh && (
          <button
            onClick={onRefresh}
            data-testid="sent-refresh"
            title="Refresh"
            className="ml-auto p-1.5 rounded-lg text-white/40 hover:text-cyan-300 hover:bg-cyan-500/10 transition-all"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Notice cards */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-8 text-white/30 text-xs">Nothing in this bucket.</div>
        )}
        {filtered.map(n => {
          const meta = TYPE_META[n.type] || TYPE_META.note;
          const Icon = meta.icon;
          const isExpanded = expanded.has(n.id);
          const responseAction = n.response?.action;
          const responseText = n.response?.text;

          const statusPill = n.acknowledged
            ? { label: 'Acknowledged', hex: '#10B981', Icon: Check }
            : n.response
              ? { label: 'Responded', hex: '#00E5FF', Icon: MessageSquare }
              : n.is_read
                ? { label: 'Read · Awaiting', hex: '#F59E0B', Icon: Eye }
                : { label: 'Delivered · Unread', hex: '#94A3B8', Icon: Send };

          const StatusIcon = statusPill.Icon;

          return (
            <div key={n.id} className="relative overflow-hidden rounded-2xl ring-1 ring-white/[0.06] bg-white/[0.02] hover:ring-white/15 transition-all" data-testid={`sent-notice-${n.id}`}>
              <span className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${meta.bar}`} />
              <button onClick={() => toggle(n.id)} className="w-full text-left p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl ring-1 ring-white/[0.08] flex items-center justify-center flex-shrink-0" style={{ background: `${meta.hex}20` }}>
                  <Icon className="w-4 h-4" style={{ color: meta.hex }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.1em] uppercase ring-1" style={{ color: meta.hex, background: `${meta.hex}12`, borderColor: `${meta.hex}30` }}>{n.type_label}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenStaff && onOpenStaff(n.staff_username); }}
                      data-testid={`sent-open-staff-${n.staff_username}`}
                      className="text-white/50 text-[10.5px] hover:text-cyan-300 hover:underline transition-colors"
                    >
                      → {n.staff_display_name || n.staff_username}
                    </button>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider ring-1" style={{ color: statusPill.hex, background: `${statusPill.hex}12`, borderColor: `${statusPill.hex}40` }}>
                      <StatusIcon className="w-2.5 h-2.5" />
                      {statusPill.label}
                    </span>
                    {n.priority === 'urgent' && <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold uppercase tracking-wider bg-rose-500/20 text-rose-300 ring-1 ring-rose-500/40">Urgent</span>}
                    {n.priority === 'high' && <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold uppercase tracking-wider bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/40">High</span>}
                  </div>
                  <p className="text-white text-sm font-medium truncate">{n.title}</p>
                  <div className="flex items-center gap-2 text-[10px] text-white/35 mt-0.5">
                    <span>Sent {new Date(n.created_at).toLocaleString('en-GB')}</span>
                    {n.read_at && <>· <span className="text-cyan-300/70">Read {new Date(n.read_at).toLocaleString('en-GB')}</span></>}
                    {n.response_at && <>· <span className="text-emerald-300/70">Replied {new Date(n.response_at).toLocaleString('en-GB')}</span></>}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-white/30 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180 text-cyan-300' : ''}`} />
              </button>

              {isExpanded && (
                <div className="border-t border-white/[0.06] p-4 space-y-3" style={{ animation: 'fadeInUp 0.15s ease-out' }}>
                  {/* Original message */}
                  <div className="rounded-xl bg-slate-950/40 ring-1 ring-white/[0.05] p-3">
                    <p className="text-white/30 text-[9px] uppercase tracking-[0.14em] font-bold mb-1.5 flex items-center gap-1"><Send className="w-2.5 h-2.5" /> Your message</p>
                    <p className="text-white/75 text-[12.5px] leading-relaxed whitespace-pre-wrap">{n.message || n.body || '—'}</p>
                    {n.meeting_date && (
                      <p className="mt-2 text-[10.5px] text-violet-300">Meeting proposed: {new Date(n.meeting_date).toLocaleString('en-GB')}</p>
                    )}
                  </div>

                  {/* Reply from staff — THE FIX */}
                  {(n.response || n.acknowledged) ? (
                    <div className="rounded-xl bg-emerald-500/[0.06] ring-1 ring-emerald-500/25 p-3">
                      <p className="text-emerald-300 text-[9px] uppercase tracking-[0.14em] font-bold mb-1.5 flex items-center gap-1.5">
                        <MessageSquare className="w-2.5 h-2.5" /> Reply from {n.staff_display_name || n.staff_username}
                        {responseAction === 'acknowledge' && <span className="ml-auto text-[9px] normal-case tracking-normal text-emerald-300/80">✓ Acknowledged</span>}
                        {responseAction === 'accept_meeting' && <span className="ml-auto text-[9px] normal-case tracking-normal text-cyan-300/80">✓ Meeting accepted</span>}
                        {responseAction === 'request_reschedule' && <span className="ml-auto text-[9px] normal-case tracking-normal text-amber-300/80">⟳ Reschedule requested</span>}
                        {responseAction === 'reply' && <span className="ml-auto text-[9px] normal-case tracking-normal text-cyan-300/80">✎ Reply</span>}
                      </p>
                      {responseText ? (
                        <p className="text-white/85 text-[13px] leading-relaxed whitespace-pre-wrap">{responseText}</p>
                      ) : (
                        <p className="text-white/40 text-[12px] italic">
                          {responseAction === 'acknowledge' ? 'Marked as acknowledged — no additional message.' : 'No message included.'}
                        </p>
                      )}
                      {n.response?.proposed_date && (
                        <p className="mt-2 text-[11px] text-amber-300">Proposed new date: {new Date(n.response.proposed_date).toLocaleString('en-GB')}</p>
                      )}
                    </div>
                  ) : n.is_read ? (
                    <div className="rounded-xl bg-amber-500/[0.05] ring-1 ring-amber-500/25 p-3 flex items-center gap-2.5">
                      <Eye className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
                      <p className="text-amber-200 text-[12px]">Read at {new Date(n.read_at).toLocaleString('en-GB')} — awaiting a reply.</p>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-white/[0.02] ring-1 ring-white/[0.06] p-3 flex items-center gap-2.5">
                      <Send className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                      <p className="text-white/50 text-[12px]">Delivered — not opened yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────  COMPOSE MODAL  ───────────────────────────── */
/* ═══════════════════════════════════════════════════════════════════
   Searchable staff picker — replaces the broken native <select>
   ═══════════════════════════════════════════════════════════════════ */
const StaffPicker = ({ value, onChange, staffList }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = React.useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = staffList.find(s => s.username === value);
  const filtered = staffList.filter(s => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (s.display_name || '').toLowerCase().includes(q)
        || (s.username || '').toLowerCase().includes(q)
        || (s.role || '').toLowerCase().includes(q);
  });

  const roleTone = (role) => {
    switch (role) {
      case 'superadmin': return { hex: '#F59E0B', label: 'Super' };
      case 'executive':  return { hex: '#8A2BE2', label: 'Exec' };
      case 'admin':      return { hex: '#00E5FF', label: 'Admin' };
      case 'manager':    return { hex: '#10B981', label: 'Manager' };
      default:           return { hex: '#94A3B8', label: 'Staff' };
    }
  };

  return (
    <div ref={rootRef} className="relative" data-testid="hr-compose-staff">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="hr-compose-staff-toggle"
        className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-black/40 border border-white/[0.08] rounded-xl text-white text-sm hover:border-cyan-400/40 focus:border-cyan-400/60 focus:outline-none transition-all"
      >
        {selected ? (
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold border flex-shrink-0"
              style={{ background: `${roleTone(selected.role).hex}18`, borderColor: `${roleTone(selected.role).hex}40`, color: roleTone(selected.role).hex }}>
              {(selected.display_name || selected.username || '?').charAt(0).toUpperCase()}
            </span>
            <span className="truncate">{selected.display_name || selected.username}</span>
            <span className="text-white/35 text-[10px] truncate">@{selected.username}</span>
          </span>
        ) : (
          <span className="text-white/40">Search staff by name, username or role…</span>
        )}
        <ChevronRight className={`w-3.5 h-3.5 text-white/40 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-2 rounded-2xl border border-white/[0.10] bg-[#0A0A12]/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden">
          <div className="p-2 border-b border-white/[0.06]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <input
                autoFocus
                data-testid="hr-compose-staff-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to filter…"
                className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/[0.06] rounded-lg text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto jewel-scroll py-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-white/40 text-xs">
                {staffList.length === 0 ? 'No staff loaded — check your access level.' : 'No matches. Try a different search.'}
              </div>
            ) : filtered.map((s) => {
              const rt = roleTone(s.role);
              const active = s.username === value;
              return (
                <button
                  key={s.username}
                  type="button"
                  data-testid={`hr-compose-staff-option-${s.username}`}
                  onClick={() => { onChange(s.username); setOpen(false); setQuery(''); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${active ? 'bg-cyan-500/[0.10]' : 'hover:bg-white/[0.04]'}`}
                >
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border flex-shrink-0"
                    style={{ background: `${rt.hex}18`, borderColor: `${rt.hex}40`, color: rt.hex }}>
                    {(s.display_name || s.username).charAt(0).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm truncate">{s.display_name || s.username}</span>
                      <span className="text-white/35 text-[10px] truncate">@{s.username}</span>
                    </div>
                  </div>
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.1em] uppercase border flex-shrink-0"
                    style={{ background: `${rt.hex}12`, borderColor: `${rt.hex}30`, color: rt.hex }}>
                    {rt.label}
                  </span>
                  {active && <Check className="w-3.5 h-3.5 text-cyan-300 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="px-3 py-2 border-t border-white/[0.06] text-[10px] text-white/40 flex justify-between">
            <span>{filtered.length} of {staffList.length}</span>
            {value && (
              <button type="button" onClick={() => { onChange(''); setQuery(''); }} className="text-rose-300 hover:text-rose-200">Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const HRComposeModal = ({ open, onOpenChange, templates, staffList, onSent, currentUser }) => {
  const [form, setForm] = useState({
    staff_username: '', type: '', title: '', description: '', priority: 'normal',
    additional_recipients: [], // For bulk send
    absence_start: '', absence_end: '', absence_reason: '', self_certified: false, fit_note: false,
    meeting_date: '', meeting_location: '', meeting_attendees: [], right_to_be_accompanied: true,
    allegations: '', evidence: '', valid_until: '', action_plan: '', appeal_deadline: '',
  });
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    // Try to restore draft from localStorage
    let draft = null;
    try { draft = JSON.parse(localStorage.getItem('hr_compose_draft') || 'null'); } catch {}
    setForm(draft || {
      staff_username: '', type: '', title: '', description: '', priority: 'normal',
      additional_recipients: [],
      absence_start: '', absence_end: '', absence_reason: '', self_certified: false, fit_note: false,
      meeting_date: '', meeting_location: '', meeting_attendees: [], right_to_be_accompanied: true,
      allegations: '', evidence: '', valid_until: '', action_plan: '', appeal_deadline: '',
    });
    setShowPreview(false);
  }, [open]);

  // Autosave draft as user types
  useEffect(() => {
    if (!open) return;
    if (!form.staff_username && !form.title && !form.description) return;
    try { localStorage.setItem('hr_compose_draft', JSON.stringify(form)); } catch {}
  }, [form, open]);

  const selectedTemplate = templates.find(t => t.type === form.type);
  const showAbsence = ['sickness', 'absence'].includes(form.type);
  const showMeeting = ['disciplinary_invite', 'investigation_invite', 'absence_review_invite'].includes(form.type);
  const showWarning = form.type?.startsWith('warning_') || form.type === 'disciplinary_invite';
  const showOutcome = form.type?.startsWith('outcome_');

  // Auto-fill title from template
  useEffect(() => {
    if (selectedTemplate && !form.title) setForm(f => ({ ...f, title: selectedTemplate.label }));
  }, [selectedTemplate?.type]);

  const send = async () => {
    if (!form.staff_username || !form.type || !form.title) { toast.error('Staff, type, and title are required'); return; }
    setBusy(true);
    try {
      const h = getAuthHeaders();
      const allRecipients = [form.staff_username, ...(form.additional_recipients || [])];
      const isBulk = allRecipients.length > 1;

      let noticeId;
      if (isBulk) {
        // Bulk send — one payload, backend fans out to each recipient
        const res = await axios.post(`${BACKEND_URL}/api/hr-notices/notice/bulk`, {
          staff_usernames: allRecipients,
          type: form.type,
          title: form.title,
          description: form.description,
          priority: form.priority,
          meeting_date: form.meeting_date || null,
          meeting_location: form.meeting_location || null,
          valid_until: form.valid_until || null,
        }, { headers: h });
        const sent = res.data?.sent_count || 0;
        const failed = res.data?.failed_count || 0;
        if (failed > 0) toast.warning(`Sent to ${sent} · ${failed} failed`);
        else toast.success(`Notice delivered to ${sent} staff`);
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/hr-notices/notice`, form, { headers: h });
        noticeId = res.data?.notice?.id;
        // Upload any pending files (only supported for single-recipient notices)
        for (const f of pendingFiles) {
          const fd = new FormData();
          fd.append('file', f);
          try { await axios.post(`${BACKEND_URL}/api/hr-notices/notice/${noticeId}/attachment`, fd, { headers: { ...h, 'Content-Type': 'multipart/form-data' } }); }
          catch { toast.error(`Upload failed: ${f.name}`); }
        }
        toast.success('Notice delivered');
      }
      setPendingFiles([]);
      try { localStorage.removeItem('hr_compose_draft'); } catch {}
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    }
    setBusy(false);
  };

  const clearDraft = () => {
    try { localStorage.removeItem('hr_compose_draft'); } catch {}
    setForm({
      staff_username: '', type: '', title: '', description: '', priority: 'normal',
      additional_recipients: [],
      absence_start: '', absence_end: '', absence_reason: '', self_certified: false, fit_note: false,
      meeting_date: '', meeting_location: '', meeting_attendees: [], right_to_be_accompanied: true,
      allegations: '', evidence: '', valid_until: '', action_plan: '', appeal_deadline: '',
    });
    toast.success('Draft cleared');
  };

  const addRecipient = (username) => {
    if (!username || username === form.staff_username) return;
    if (form.additional_recipients.includes(username)) return;
    setForm({ ...form, additional_recipients: [...form.additional_recipients, username] });
  };
  const removeRecipient = (username) => {
    setForm({ ...form, additional_recipients: form.additional_recipients.filter(u => u !== username) });
  };

  const totalRecipients = 1 + (form.additional_recipients?.length || 0);
  const priorityMeta = {
    normal: { hex: '#94A3B8', label: 'Normal' },
    high:   { hex: '#F59E0B', label: 'High' },
    urgent: { hex: '#F43F5E', label: 'Urgent · immediate action' },
  };

  return (
    <JewelModal open={open} onOpenChange={onOpenChange} icon={Send} title="New HR Notice" subtitle="Sickness · absence · warning · meeting · outcome"
      accent="cyan" size="2xl" testid="hr-compose-modal"
      footer={<>
        <JewelButton variant="ghost" onClick={clearDraft}>Clear draft</JewelButton>
        <JewelButton variant="ghost" onClick={() => setShowPreview(p => !p)} data-testid="hr-compose-preview-btn">{showPreview ? 'Edit' : 'Preview'}</JewelButton>
        <JewelButton variant="ghost" onClick={() => onOpenChange(false)}>Cancel</JewelButton>
        <JewelButton accent="cyan" onClick={send} loading={busy} disabled={busy} icon={Send} data-testid="hr-compose-send-btn">
          {totalRecipients > 1 ? `Send to ${totalRecipients} staff` : 'Send notice'}
        </JewelButton>
      </>}
    >
      {showPreview ? (
        <div className="space-y-3" data-testid="hr-compose-preview">
          <div className="relative overflow-hidden rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-cyan-500/[0.05] to-violet-500/[0.03] p-5">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-cyan-300/70 font-medium">Preview · what the recipient will see</p>
                <h3 className="text-white text-lg font-medium tracking-tight mt-1">{form.title || 'Untitled notice'}</h3>
                <p className="text-white/50 text-[11px] mt-1">{templates.find(t => t.type === form.type)?.label || 'No type'} · From {currentUser?.display_name || 'HR'}</p>
              </div>
              <div className="flex flex-col gap-1 items-end">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.14em] uppercase border"
                  style={{ background: `${priorityMeta[form.priority].hex}18`, borderColor: `${priorityMeta[form.priority].hex}40`, color: priorityMeta[form.priority].hex }}>
                  {priorityMeta[form.priority].label}
                </span>
                {totalRecipients > 1 && (
                  <span className="text-[10px] tracking-[0.14em] uppercase text-cyan-300">Bulk · {totalRecipients} recipients</span>
                )}
              </div>
            </div>
            <p className="text-white/80 text-sm whitespace-pre-wrap leading-relaxed">{form.description || <span className="italic text-white/30">No description</span>}</p>
            {form.meeting_date && (
              <div className="mt-3 p-2.5 rounded-xl bg-black/30 border border-white/[0.06] text-[11px] text-white/70">
                📅 Meeting · {new Date(form.meeting_date).toLocaleString('en-GB')}{form.meeting_location ? ` · ${form.meeting_location}` : ''}
              </div>
            )}
            {form.valid_until && (
              <p className="text-[10px] text-white/40 mt-2">Valid until: {new Date(form.valid_until).toLocaleDateString('en-GB')}</p>
            )}
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05] text-[11px] text-white/60">
            <span className="text-white/40 tracking-[0.14em] uppercase text-[9px]">Recipients:</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {[form.staff_username, ...form.additional_recipients].filter(Boolean).map(u => (
                <span key={u} className="px-2 py-0.5 rounded-full text-[10px] bg-cyan-500/10 border border-cyan-400/25 text-cyan-200 font-mono">@{u}</span>
              ))}
            </div>
          </div>
        </div>
      ) : (
      <div className="space-y-4">
        <TemplateLibrary
          builtInTemplates={templates}
          currentForm={form}
          onApply={(patch) => setForm(f => ({ ...f, ...patch }))}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <JewelField label="Staff member" required>
            <StaffPicker
              value={form.staff_username}
              onChange={(u) => setForm({ ...form, staff_username: u })}
              staffList={staffList}
            />
          </JewelField>
          <JewelField label="Notice type" required>
            <JewelSelect value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="hr-compose-type">
              <option value="">Pick a template…</option>
              {templates.map(t => <option key={t.type} value={t.type}>{t.label}{t.requires_ack ? ' · ack required' : ''}</option>)}
            </JewelSelect>
          </JewelField>
        </div>
        <JewelField label="Title" required>
          <JewelInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short summary…" data-testid="hr-compose-title" />
        </JewelField>
        <JewelField label="Description">
          <JewelTextarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Full context, next steps, or supporting information…" />
        </JewelField>

        {/* Priority + Additional recipients */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <JewelField label="Priority">
            <div className="flex gap-1.5" data-testid="hr-compose-priority">
              {Object.entries(priorityMeta).map(([id, meta]) => {
                const active = form.priority === id;
                return (
                  <button key={id} type="button" onClick={() => setForm({ ...form, priority: id })}
                    data-testid={`hr-priority-${id}`}
                    className="flex-1 px-3 py-2 rounded-xl text-[10px] font-semibold tracking-[0.14em] uppercase transition-all border"
                    style={{
                      background: active ? `${meta.hex}18` : 'rgba(255,255,255,0.02)',
                      borderColor: active ? `${meta.hex}45` : 'rgba(255,255,255,0.06)',
                      color: active ? meta.hex : 'rgba(255,255,255,0.5)',
                    }}>
                    {meta.label.split(' ·')[0]}
                  </button>
                );
              })}
            </div>
          </JewelField>
          <JewelField label={`Additional recipients${form.additional_recipients.length > 0 ? ` (${form.additional_recipients.length})` : ''}`}>
            <div className="space-y-1.5">
              <StaffPicker
                value=""
                onChange={(u) => addRecipient(u)}
                staffList={staffList.filter(s => s.username !== form.staff_username && !form.additional_recipients.includes(s.username))}
              />
              {form.additional_recipients.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="additional-recipients-chips">
                  {form.additional_recipients.map(u => {
                    const s = staffList.find(x => x.username === u);
                    return (
                      <span key={u} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-[10px] font-medium border border-cyan-400/25 bg-cyan-500/10 text-cyan-200">
                        {s?.display_name || u}
                        <button type="button" onClick={() => removeRecipient(u)} className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-rose-500/25 hover:text-rose-200">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </JewelField>
        </div>

        {showAbsence && (
          <div className="p-4 rounded-2xl bg-amber-500/[0.04] border border-amber-500/20 space-y-3">
            <p className="text-[9px] tracking-[0.2em] uppercase text-amber-300">Absence details</p>
            <div className="grid grid-cols-2 gap-3">
              <JewelField label="Start"><JewelInput type="date" value={form.absence_start} onChange={(e) => setForm({ ...form, absence_start: e.target.value })} /></JewelField>
              <JewelField label="End"><JewelInput type="date" value={form.absence_end} onChange={(e) => setForm({ ...form, absence_end: e.target.value })} /></JewelField>
            </div>
            <JewelField label="Reason / notes"><JewelInput value={form.absence_reason} onChange={(e) => setForm({ ...form, absence_reason: e.target.value })} /></JewelField>
            <div className="flex gap-4 flex-wrap">
              <label className="inline-flex items-center gap-2 text-white/70 text-xs">
                <input type="checkbox" checked={form.self_certified} onChange={(e) => setForm({ ...form, self_certified: e.target.checked })} className="w-4 h-4 accent-amber-400" /> Self-certified
              </label>
              <label className="inline-flex items-center gap-2 text-white/70 text-xs">
                <input type="checkbox" checked={form.fit_note} onChange={(e) => setForm({ ...form, fit_note: e.target.checked })} className="w-4 h-4 accent-amber-400" /> Fit note received
              </label>
            </div>
          </div>
        )}

        {showMeeting && (
          <div className="p-4 rounded-2xl bg-cyan-500/[0.04] border border-cyan-500/20 space-y-3">
            <p className="text-[9px] tracking-[0.2em] uppercase text-cyan-300">Meeting details</p>
            <div className="grid grid-cols-2 gap-3">
              <JewelField label="Date & time" required><JewelInput type="datetime-local" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} /></JewelField>
              <JewelField label="Location / link"><JewelInput value={form.meeting_location} onChange={(e) => setForm({ ...form, meeting_location: e.target.value })} placeholder="Meeting room / Video link" /></JewelField>
            </div>
            <JewelField label="Attendees (comma separated)">
              <JewelInput value={form.meeting_attendees.join(', ')} onChange={(e) => setForm({ ...form, meeting_attendees: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} placeholder="e.g. HR lead, line manager, note-taker" />
            </JewelField>
            <label className="inline-flex items-center gap-2 text-white/70 text-xs">
              <input type="checkbox" checked={form.right_to_be_accompanied} onChange={(e) => setForm({ ...form, right_to_be_accompanied: e.target.checked })} className="w-4 h-4 accent-cyan-400" /> Include statutory "right to be accompanied" clause
            </label>
          </div>
        )}

        {showWarning && (
          <div className="p-4 rounded-2xl bg-rose-500/[0.04] border border-rose-500/20 space-y-3">
            <p className="text-[9px] tracking-[0.2em] uppercase text-rose-300">Allegations & evidence</p>
            <JewelField label="Allegations"><JewelTextarea rows={3} value={form.allegations} onChange={(e) => setForm({ ...form, allegations: e.target.value })} placeholder="Describe the alleged conduct or performance concerns…" /></JewelField>
            <JewelField label="Evidence / context"><JewelTextarea rows={2} value={form.evidence} onChange={(e) => setForm({ ...form, evidence: e.target.value })} placeholder="Witness statements, dates, screenshots, prior notes…" /></JewelField>
            <JewelField label="Warning valid until"><JewelInput type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></JewelField>
          </div>
        )}

        {showOutcome && (
          <div className="p-4 rounded-2xl bg-emerald-500/[0.04] border border-emerald-500/20 space-y-3">
            <p className="text-[9px] tracking-[0.2em] uppercase text-emerald-300">Outcome plan</p>
            <JewelField label="Action plan"><JewelTextarea rows={3} value={form.action_plan} onChange={(e) => setForm({ ...form, action_plan: e.target.value })} placeholder="Improvement goals, milestones, review dates…" /></JewelField>
            <JewelField label="Appeal deadline"><JewelInput type="date" value={form.appeal_deadline} onChange={(e) => setForm({ ...form, appeal_deadline: e.target.value })} /></JewelField>
          </div>
        )}

        {/* Attachments */}
        <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06] space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 flex items-center gap-1"><FileText className="w-3 h-3" /> Attachments · PDF, images, docs</p>
            <input ref={fileInputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.txt" onChange={(e) => setPendingFiles(prev => [...prev, ...Array.from(e.target.files || [])])} className="hidden" data-testid="hr-attach-input" />
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/[0.08] border border-cyan-500/25 text-cyan-300 hover:bg-cyan-500/[0.15] text-[11px] font-medium transition-all">
              <Plus className="w-3 h-3" /> Attach file
            </button>
          </div>
          {pendingFiles.length > 0 && (
            <div className="space-y-1.5">
              {pendingFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <FileText className="w-3.5 h-3.5 text-cyan-300 flex-shrink-0" />
                  <span className="text-white/80 text-xs truncate flex-1">{f.name}</span>
                  <span className="text-white/40 text-[10px] font-mono">{(f.size / 1024).toFixed(0)} KB</span>
                  <button onClick={() => setPendingFiles(prev => prev.filter((_, x) => x !== i))} className="w-6 h-6 rounded-md text-white/40 hover:text-rose-300 hover:bg-rose-500/[0.10] flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </JewelModal>
  );
};

/* ─────────────────────────────  MANAGER DASHBOARD  ───────────────────────────── */
const ManagerDashboard = ({ data, onOpen, onOpenStaff }) => {
  if (!data) return <div className="text-center py-14 text-white/40 text-sm">Loading dashboard…</div>;
  const t = data.totals || {};
  const kpis = [
    { label: 'Total cases',        value: t.total_cases || 0,          icon: FileText,    hex: '#00E5FF' },
    { label: 'Awaiting ack',       value: t.awaiting_ack || 0,         icon: AlertCircle, hex: '#F59E0B' },
    { label: 'Upcoming meetings',  value: t.upcoming_meetings || 0,    icon: Calendar,    hex: '#8A2BE2' },
    { label: 'Active warnings',    value: t.active_warnings || 0,      icon: ShieldAlert, hex: '#F43F5E' },
    { label: 'Expiring ≤ 30d',     value: t.warnings_expiring_soon || 0, icon: Clock,     hex: '#EC4899' },
    { label: 'Absences · 30d',     value: t.recent_absences || 0,      icon: Stethoscope, hex: '#F97316' },
  ];

  const CaseRow = ({ n, showExpiry, showDaysUntil }) => {
    const meta = TYPE_META[n.type] || TYPE_META.note;
    const Icon = meta.icon;
    return (
      <button onClick={() => onOpen(n)} className="w-full text-left flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.15] transition-all">
        <div className="w-9 h-9 rounded-xl border border-white/[0.08] flex items-center justify-center flex-shrink-0" style={{ background: `${meta.hex}20` }}>
          <Icon className="w-4 h-4" style={{ color: meta.hex }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{n.title}</p>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenStaff && onOpenStaff(n.staff_username); }}
            data-testid={`dashboard-open-staff-${n.staff_username}`}
            className="text-white/45 text-[10px] mt-0.5 hover:text-cyan-300 hover:underline transition-colors"
          >
            {n.staff_display_name} · {n.type_label}
          </button>
        </div>
        {showDaysUntil !== undefined && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${n.days_until <= 1 ? 'bg-rose-500/[0.10] border border-rose-500/25 text-rose-300 animate-pulse' : n.days_until <= 3 ? 'bg-amber-500/[0.10] border border-amber-500/25 text-amber-300' : 'bg-white/[0.03] border border-white/[0.08] text-white/60'}`}>
            <Clock className="w-2.5 h-2.5" /> {n.days_until === 0 ? 'Today' : `${n.days_until}d`}
          </span>
        )}
        {showExpiry && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${n.days_remaining <= 7 ? 'bg-rose-500/[0.10] border border-rose-500/25 text-rose-300 animate-pulse' : 'bg-amber-500/[0.10] border border-amber-500/25 text-amber-300'}`}>
            <Clock className="w-2.5 h-2.5" /> {n.days_remaining}d left
          </span>
        )}
      </button>
    );
  };

  const Section = ({ title, icon: SIcon, hex, items, empty, showExpiry, showDaysUntil }) => (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="pointer-events-none absolute -top-16 right-1/4 w-48 h-32 rounded-full blur-[70px] opacity-30" style={{ background: hex }} />
      <div className="relative flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg border border-white/[0.08] flex items-center justify-center" style={{ background: `${hex}20` }}>
            <SIcon className="w-3.5 h-3.5" style={{ color: hex }} />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Live</p>
            <h3 className="text-white text-sm font-medium">{title}</h3>
          </div>
        </div>
        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold" style={{ background: `${hex}15`, color: hex, border: `1px solid ${hex}30` }}>{items.length}</span>
      </div>
      <div className="relative space-y-1.5 max-h-72 overflow-y-auto jewel-scroll pr-1">
        {items.length === 0 ? <p className="text-center text-white/30 text-xs italic py-6">{empty}</p> : items.map(n => <CaseRow key={n.id} n={n} showExpiry={showExpiry} showDaysUntil={showDaysUntil} />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 group hover:border-white/[0.15] transition-all">
            <div className="pointer-events-none absolute -top-8 -right-8 w-20 h-20 rounded-full blur-3xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ background: k.hex }} />
            <div className="relative">
              <k.icon className="w-4 h-4 mb-2" style={{ color: k.hex }} />
              <p className="text-2xl font-light text-white tabular-nums leading-none">{k.value}</p>
              <p className="text-[9px] tracking-[0.15em] uppercase text-white/40 mt-1.5">{k.label}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Section title="Awaiting acknowledgement" icon={AlertCircle} hex="#F59E0B" items={data.awaiting_ack || []} empty="No pending acknowledgements" />
        <Section title="Upcoming meetings · 30d" icon={Calendar} hex="#8A2BE2" items={data.upcoming_meetings || []} empty="No meetings scheduled" showDaysUntil />
        <Section title="Warnings expiring soon" icon={ShieldAlert} hex="#F43F5E" items={data.warnings_expiring || []} empty="No warnings expiring" showExpiry />
        <Section title="Recent absences · 30d" icon={Stethoscope} hex="#F97316" items={data.recent_absences || []} empty="No recent absences" />
      </div>
    </div>
  );
};

export default HRInboxPage;
